/**
 * Financial Goal Card Component
 * Displays a pending financial goal with confirmation button and progress bar
 * Supports arbitrage goals with special urgent styling
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import ProgressBar from '../goals/ProgressBar';
import type { GoalProposal, ArbitrageMetadata } from '../../api/types';

interface FinancialGoalCardProps {
  financialGoal: GoalProposal & {
    current_amount?: number;
    progress_percentage?: number;
    status?: 'active' | 'completed' | 'archived';
  };
  onConfirm: () => void;
  isLoading?: boolean;
  isConfirmed?: boolean;
  confirmedGoalId?: string;
  onViewGoal?: (goalId: string) => void;
  onExecuteArbitrage?: (goalId: string) => void;
  goalIndex?: number; // Position in roadmap (0-based)
}

export default function FinancialGoalCard({
  financialGoal,
  onConfirm,
  isLoading = false,
  isConfirmed = false,
  confirmedGoalId,
  onViewGoal,
  onExecuteArbitrage,
  goalIndex,
}: FinancialGoalCardProps) {
  const currentAmount = financialGoal.current_amount || 0;
  const targetAmount = financialGoal.target_amount || 0;
  const isCompleted = financialGoal.status === 'completed';
  const isArbitrage = financialGoal.is_arbitrage || false;
  const isUrgent = financialGoal.is_urgent || false;
  const arbitrageMetadata = financialGoal.arbitrage_metadata;

  // Determine container style based on goal type
  const getContainerStyle = () => {
    if (isCompleted) return [styles.container, styles.completedContainer];
    if (isArbitrage) return [styles.container, styles.arbitrageContainer];
    return [styles.container];
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('fr-FR') + '€';
  };

  return (
    <View style={getContainerStyle()}>
      {/* Priority badge for roadmap */}
      {goalIndex !== undefined && (
        <View style={[styles.priorityBadge, isArbitrage && styles.priorityBadgeUrgent]}>
          <Text style={styles.priorityBadgeText}>
            {isArbitrage ? '🔥 URGENT' : `📌 Objectif ${goalIndex + 1}`}
          </Text>
        </View>
      )}

      <View style={styles.titleRow}>
        <Text style={[styles.title, isArbitrage && styles.arbitrageTitle]}>
          {financialGoal.title}
        </Text>
        {isCompleted && (
          <View style={styles.completedBadge}>
            <Text style={styles.completedBadgeText}>✓ Complété</Text>
          </View>
        )}
      </View>

      {/* Explanation text */}
      {financialGoal.explanation && !isConfirmed && (
        <Text style={styles.explanation}>
          💡 {financialGoal.explanation}
        </Text>
      )}

      {financialGoal.target_amount && (
        <Text style={styles.detail}>
          Montant : {formatCurrency(financialGoal.target_amount)}
        </Text>
      )}

      {/* Arbitrage-specific info */}
      {isArbitrage && arbitrageMetadata && (
        <>
          <Text style={styles.arbitrageDetail}>
            💰 Gain mensuel : +{formatCurrency(arbitrageMetadata.monthly_cashflow_gain)}/mois
          </Text>
          <Text style={styles.arbitrageDetail}>
            📈 ROI garanti : {arbitrageMetadata.roi_equivalent}%
          </Text>
        </>
      )}

      {/* Non-arbitrage details - only show if monthly_effort is positive */}
      {!isArbitrage && financialGoal.monthly_effort && financialGoal.monthly_effort > 0 && (
        <Text style={styles.detail}>
          Effort mensuel : {formatCurrency(financialGoal.monthly_effort)}/mois
        </Text>
      )}

      {financialGoal.deadline && (
        <Text style={styles.detail}>
          {financialGoal.months_needed
            ? `Délai : ${financialGoal.months_needed} mois`
            : `Date cible : ${new Date(financialGoal.deadline).toLocaleDateString('fr-FR')}`}
        </Text>
      )}

      {/* Show progress bar IMMEDIATELY after confirmation */}
      {isConfirmed && targetAmount > 0 && (
        <View style={styles.progressSection}>
          <ProgressBar
            current={currentAmount}
            target={targetAmount}
            showAmounts={true}
          />
        </View>
      )}

      {/* Show "View Goal" button if confirmed and we have the goal ID */}
      {isConfirmed && confirmedGoalId && onViewGoal && (
        <TouchableOpacity
          style={styles.viewGoalButton}
          onPress={() => onViewGoal(confirmedGoalId)}
        >
          <Text style={styles.viewGoalText}>
            ✓ Objectif ajouté  •  Voir l'objectif →
          </Text>
        </TouchableOpacity>
      )}

      {/* Show "Execute Arbitrage" button for confirmed arbitrage goals */}
      {isConfirmed && isArbitrage && confirmedGoalId && onExecuteArbitrage && !isCompleted && (
        <TouchableOpacity
          style={styles.executeArbitrageButton}
          onPress={() => onExecuteArbitrage(confirmedGoalId)}
        >
          <Text style={styles.executeArbitrageText}>
            🔥 J'ai remboursé cette dette
          </Text>
        </TouchableOpacity>
      )}

      {/* Show confirm button if not yet confirmed */}
      {!isConfirmed && (
        <TouchableOpacity
          style={[
            styles.button,
            isArbitrage && styles.arbitrageButton,
            isLoading && styles.buttonDisabled
          ]}
          onPress={onConfirm}
          disabled={isLoading}
        >
          <Text style={styles.buttonText}>
            {isLoading ? 'Ajout en cours...' : '➕ Ajouter à mes objectifs'}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.primary + '10', // 10% opacity
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
    borderRadius: 12,
    padding: spacing.md,
    marginVertical: spacing.sm,
  },
  completedContainer: {
    backgroundColor: colors.success + '10', // 10% opacity
    borderLeftColor: colors.success,
  },
  arbitrageContainer: {
    backgroundColor: colors.error + '10', // 10% opacity - urgent red
    borderLeftColor: colors.error,
    borderWidth: 1,
    borderColor: colors.error + '30',
  },
  priorityBadge: {
    alignSelf: 'flex-start',
    backgroundColor: colors.primary + '20',
    borderRadius: 12,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    marginBottom: spacing.sm,
  },
  priorityBadgeUrgent: {
    backgroundColor: colors.error + '20',
  },
  priorityBadgeText: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  title: {
    flex: 1,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  arbitrageTitle: {
    color: colors.error,
  },
  completedBadge: {
    backgroundColor: colors.success,
    borderRadius: 12,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    marginLeft: spacing.sm,
  },
  completedBadgeText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
  },
  explanation: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontStyle: 'italic',
    marginBottom: spacing.sm,
    lineHeight: typography.fontSize.sm * 1.4,
  },
  detail: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  arbitrageDetail: {
    fontSize: typography.fontSize.sm,
    color: colors.success,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing.xs,
  },
  button: {
    backgroundColor: colors.primary,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginTop: spacing.md,
    alignItems: 'center',
  },
  arbitrageButton: {
    backgroundColor: colors.error,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
  },
  progressSection: {
    marginTop: spacing.md,
    marginBottom: spacing.xs,
  },
  viewGoalButton: {
    backgroundColor: colors.success + '15', // 15% opacity of success color
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginTop: spacing.md,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.success,
  },
  viewGoalText: {
    color: colors.success,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
  },
  executeArbitrageButton: {
    backgroundColor: colors.error + '15',
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginTop: spacing.sm,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.error,
  },
  executeArbitrageText: {
    color: colors.error,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.bold,
  },
});
